import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Calendar, Check, Eye, Edit, Trash2, FileText, Clock, Undo2 } from "lucide-react";
import { formatCurrency, formatDate, statusConfig } from "./utils";

export function LancamentosTable({ items, isReceber, selectedIds, setSelectedIds, onBaixa, onView, onEdit, onDelete, onReverter }) {
  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]">
                <Checkbox
                  checked={items.length > 0 && items.every(i => selectedIds.has(i.id))}
                  onCheckedChange={(v) => {
                    if (v) setSelectedIds(new Set(items.map(i => i.id)));
                    else setSelectedIds(new Set());
                  }}
                  data-testid="checkbox-select-all"
                />
              </TableHead>
              <TableHead>Vencimento</TableHead>
              {isReceber ? (
                <>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Nº de Proposta</TableHead>
                </>
              ) : (
                <>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Documento</TableHead>
                </>
              )}
              <TableHead>Parcela</TableHead>
              <TableHead className="text-right">Valor</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[120px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => {
              const StatusIcon = statusConfig[item.status_display]?.icon || Clock;
              const checked = selectedIds.has(item.id);
              return (
                <TableRow key={item.id} className={item.status_display === "vencido" ? "bg-red-50" : ""}>
                  <TableCell>
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) => {
                        const next = new Set(selectedIds);
                        if (v) next.add(item.id); else next.delete(item.id);
                        setSelectedIds(next);
                      }}
                      data-testid={`checkbox-${item.id}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-slate-400" />
                      {formatDate(item.data_vencimento)}
                    </div>
                  </TableCell>
                  {isReceber ? (
                    <>
                      <TableCell>
                        <span className="line-clamp-1">{item.cliente_nome || "-"}</span>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {item.proposta_numero ? `#${item.proposta_numero}` : "-"}
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>
                        <span className="line-clamp-1">{item.descricao}</span>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{item.documento || "-"}</TableCell>
                    </>
                  )}
                  <TableCell>
                    {item.parcela ? (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${
                          item.modo_origem === "recorrente"
                            ? "bg-indigo-50 text-indigo-700 border border-indigo-200"
                            : item.modo_origem === "parcelado" || item.grupo_id
                            ? "bg-sky-50 text-sky-700 border border-sky-200"
                            : item.residuo_de
                            ? "bg-amber-50 text-amber-700 border border-amber-200"
                            : "bg-slate-100 text-slate-700"
                        }`}
                        title={
                          item.modo_origem === "recorrente"
                            ? "Lançamento recorrente"
                            : item.grupo_id
                            ? "Parcelado"
                            : item.residuo_de
                            ? "Resíduo de baixa parcial"
                            : ""
                        }
                      >
                        {item.modo_origem === "recorrente" && "↻ "}
                        {item.residuo_de && "⚠ "}
                        {item.parcela}
                      </span>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell className={`text-right font-bold ${isReceber ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(item.valor)}
                    {item.correcao && (
                      <span className="block text-[10px] font-semibold text-amber-700" title={`${item.correcao.dias_atraso} dia(s) de atraso — juros ${formatCurrency(item.correcao.juros_calculado)} + multa ${formatCurrency(item.correcao.multa_calculada)}`} data-testid={`valor-corrigido-${item.id}`}>
                        corrigido: {formatCurrency(item.correcao.valor_corrigido)}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={statusConfig[item.status_display]?.class || "bg-slate-100"}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {statusConfig[item.status_display]?.label || item.status}
                    </Badge>
                    {item.is_transferencia && (
                      <span className="block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 w-fit" data-testid={`badge-transferencia-${item.id}`}>
                        ⇄ Transferência
                      </span>
                    )}
                    {item.baixa_sem_conta && !item.banco_id && (
                      <span className="block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-300 w-fit" title="Baixa sem conta bancária identificada — edite e informe o banco" data-testid={`badge-sem-conta-${item.id}`}>
                        ⚠ Sem conta
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {item.status === "pendente" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-green-600"
                          onClick={() => onBaixa(item)}
                          title={isReceber ? "Receber" : "Pagar"}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                      )}
                      {(item.status === "pago" || item.status === "recebido") && onReverter && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-amber-600"
                          onClick={() => onReverter(item)}
                          title="Reverter baixa (voltar para Pendente)"
                          data-testid={`btn-reverter-${item.id}`}
                        >
                          <Undo2 className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onView(item)}
                        title="Visualizar"
                        data-testid={`btn-view-${item.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(item)}
                        title="Editar"
                        data-testid={`btn-edit-${item.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500"
                        onClick={() => onDelete(item)}
                        title="Excluir"
                        data-testid={`btn-delete-${item.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                  <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  Nenhum lançamento encontrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
